<template>
  <CCard v-if="tipo">
    <CCardHeader>Mapeamento de campos</CCardHeader>

    <CCardBody>
      <p class="mapeamento__intro">
        De qual campo do serviço sai cada dado do cadastro. Nada é adivinhado:
        o que não estiver mapeado aqui não é aproveitado — e como cada conta
        nomeia os próprios campos, o nome deles só existe do lado de lá.
      </p>

      <div class="mapeamento__resumo">
        <div class="mapeamento__numero">
          <strong>{{ totalMapeado }}</strong>
          <span>{{ totalMapeado === 1 ? 'campo mapeado' : 'campos mapeados' }}</span>
        </div>

        <div v-if="camposRemotosTotal" class="mapeamento__numero">
          <strong>{{ camposRemotosTotal }}</strong>
          <span>campos conhecidos do serviço</span>
        </div>

        <div class="mapeamento__acoes">
          <CButton
            v-if="permiteBusca"
            color="info"
            variant="outline"
            size="sm"
            :disabled="buscando"
            @click="buscar"
          >
            <fa-icon icon="download" />
            {{ buscando ? 'Buscando...' : 'Buscar campos no serviço' }}
          </CButton>
        </div>
      </div>

      <p v-if="camposRemotosEm" class="mapeamento__quando">
        Campos buscados em {{ dataHora(camposRemotosEm) }}.
        <template v-if="permiteBusca">
          A busca lê os campos da conta com a chave de API configurada e os
          oferece na lista abaixo, com o nome que eles têm lá.
        </template>
      </p>

      <CAlert v-if="aviso" show :color="avisoCor">{{ aviso }}</CAlert>

      <div v-if="carregando" class="mapeamento__carregando">Carregando o mapeamento...</div>

      <!--
        O editor renderiza aqui dentro, não num modal: esta seção já é a do
        mapeamento daquele conector, e uma camada por cima só esconderia o
        contexto que a explica.
      -->
      <EditorCampos
        v-else-if="editor"
        :embutido="true"
        class="mapeamento__editor"
        :tipo="editor.tipo"
        :campos="editor.campos"
        :formatos="editor.formatos"
        :funcoes-catalogo="editor.funcoes"
        :operadores="editor.operadores"
        :on-submit="salvarMapeamento"
        @descartar="carregarEditor"
      />
    </CCardBody>

  </CCard>
</template>

<script>
/*
 * A seção de mapeamento de campos de uma integração.
 *
 * O DE/PARA sempre existiu, mas em outro módulo — Configurações › Campos de
 * Integração —, sob um tipo cujo nome quem instala o conector não tem por que
 * conhecer. Quem configurava a integração não sabia que precisava mapear, quem
 * mapeava não sabia para qual integração aquilo servia, e quem tinha só a
 * permissão de Integrações esbarrava num "sem acesso".
 *
 * Aqui o editor não é um destino nem um modal: é o conteúdo desta seção. A
 * lista de mapeamentos aparece junto da configuração que a produz, e o mesmo
 * componente do outro módulo é reusado no modo embutido.
 */
import { get, put } from '@/helpers/apiRequest';
import EditorCampos from '@/views/CamposIntegracao/components/Edit';

export default {
  name: 'MapeamentoCampos',
  components: { EditorCampos },
  props: {
    integracaoId: { type: [Number, String], default: null },
    tipo: { type: String, default: '' },
    permiteBusca: { type: Boolean, default: false },
    camposRemotosTotal: { type: Number, default: 0 },
    camposRemotosEm: { type: String, default: '' },
  },
  data() {
    return {
      totalMapeado: 0,
      editor: null,
      carregando: false,
      buscando: false,
      aviso: '',
      avisoCor: 'info',
    };
  },
  mounted() {
    this.carregarEditor();
    this.buscarSeAindaNaoBuscou();
  },
  watch: {
    /*
     * O servidor esquece os campos guardados quando a credencial muda, e é
     * assim que a troca de token chega até aqui com a tela já aberta: a
     * contagem volta a zero. Buscar de novo nesse momento é o que impede o
     * mapeamento de continuar oferecendo os campos da conta anterior.
     */
    camposRemotosTotal(total, anterior) {
      if (total === 0 && anterior > 0) {
        this.buscarSeAindaNaoBuscou();
      }
    },
  },
  methods: {
    /*
     * Tudo que o editor precisa vem numa resposta só, pela rota da própria
     * integração — é ela que dispensa a permissão do módulo de campos.
     */
    carregarEditor() {
      this.carregando = true;
      this.aviso = '';

      get(`/admin/integracoes/${this.integracaoId}/mapeamento`)
        .then((resposta) => {
          this.editor = resposta.data;
          this.totalMapeado = (resposta.data.campos || []).length;
        })
        .catch((erro) => {
          this.editor = null;
          this.aviso = erro.message || 'Não foi possível carregar o mapeamento.';
          this.avisoCor = 'danger';
        })
        .finally(() => {
          this.carregando = false;
        });
    },

    salvarMapeamento(campos) {
      put(`/admin/integracoes/${this.integracaoId}/mapeamento`, campos)
        .then((resposta) => {
          this.$swal({
            title: 'Sucesso!',
            text: (resposta && resposta.message) || 'Mapeamento salvo.',
            icon: 'success',
          });

          // Recarrega para a lista refletir o que de fato ficou gravado — a
          // ordem é atribuída no servidor.
          this.carregarEditor();
        })
        .catch((erro) => {
          this.$swal({
            title: 'Ops!',
            text: erro.message,
            icon: 'error',
          });
        });
    },

    /**
     * Traz os campos do serviço sozinho, quando ainda não há nenhum guardado.
     *
     * Buscar é o primeiro passo de todo mapeamento, e deixá-lo num botão fazia
     * com que a lista de origem aparecesse incompleta para quem não soubesse
     * que precisava clicar. Quem esquece a lista é o servidor, quando a
     * credencial muda — então "não há nada guardado" quer dizer tanto "nunca
     * buscou" quanto "o token é outro agora".
     *
     * Em silêncio de propósito: é conveniência, e o botão continua ali para
     * quem quiser forçar e ver o que aconteceu.
     */
    buscarSeAindaNaoBuscou() {
      if (!this.permiteBusca || !this.integracaoId || this.camposRemotosTotal > 0) {
        return;
      }

      this.buscar({ silencioso: true });
    },

    buscar({ silencioso = false } = {}) {
      this.buscando = true;
      this.aviso = '';

      get(`/admin/integracoes/${this.integracaoId}/campos-remotos`)
        .then((resposta) => {
          if (!silencioso) {
            this.aviso = (resposta && resposta.message) || 'Campos atualizados.';
            this.avisoCor = 'success';
          }

          // O dicionário de origem do editor acabou de crescer; sem recarregar,
          // os campos recém-descobertos não apareceriam na lista de seleção.
          this.carregarEditor();
          this.$emit('campos-buscados');
        })
        .catch((erro) => {
          // Na busca automática o erro não vira alarme: quem abriu a tela não
          // pediu nada, e o botão continua disponível para tentar de novo.
          if (silencioso) {
            return;
          }

          this.aviso = erro.message || 'Não foi possível buscar os campos.';
          this.avisoCor = 'danger';
        })
        .finally(() => {
          this.buscando = false;
        });
    },

    dataHora(valor) {
      if (!valor) {
        return '—';
      }

      const data = new Date(String(valor).replace(' ', 'T'));

      return Number.isNaN(data.getTime())
        ? String(valor)
        : data.toLocaleString('pt-BR');
    },
  },
};
</script>

<style scoped>
.mapeamento__intro {
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 12px;
}

.mapeamento__resumo {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 8px;
}

.mapeamento__numero {
  align-items: baseline;
  display: flex;
  gap: 6px;
}

.mapeamento__numero strong {
  color: #3a3e81;
  font-size: 20px;
}

.mapeamento__numero span {
  color: #6b7280;
  font-size: 12px;
}

.mapeamento__quando {
  color: #9ca3af;
  font-size: 11px;
  margin-bottom: 12px;
}

.mapeamento__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.mapeamento__carregando {
  color: #6b7280;
  font-size: 13px;
  padding: 16px 0;
}

/*
 * O editor nasceu dentro de um modal, e trouxe de lá duas coisas que não valem
 * aqui: o cartão próprio — que dentro de um CCard vira cartão dentro de cartão —
 * e o `max-height: 90vh` com rolagem interna, que o modal usa para manter o
 * rodapé à vista. Numa página, esse limite produz uma barra de rolagem dentro
 * de outra; quem rola é a página.
 */
/*
 * Filho DIRETO do wrapper embutido, e não qualquer `.modalbox` descendente: o
 * editor abre, por dentro, o modal de seleção de campo — que é um modal de
 * verdade e usa as mesmas classes. Um seletor solto zerava o respiro dele
 * também, e a caixa de busca encostava na borda.
 */
.mapeamento__editor >>> .editor-embutido > .modalbox,
.mapeamento__editor >>> .editor-embutido > .modalbox > .modalbox-content {
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.mapeamento__editor >>> .editor-embutido > .modalbox > .modalbox-content {
  max-height: none;
  overflow-y: visible;
}
</style>
